import { Anchor, Box, Breadcrumbs, Button, Card, Collapse, Grid, Group, Text } from '@mantine/core';
import { IconArrowLeft, IconChevronDown, IconChevronUp, IconPlayerStop, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import useIsSuperAdmin from '../../../helpers/useIsSuperAdmin';
import { useNavigate, useParams } from 'react-router';
import AbTestConfiguration from '../../../components/AbTest/AbTestConfiguration';
import AbTestSelector from '../../../components/AbTest/AbTestSelector';
import AbTestVisualization from '../../../components/AbTest/AbTestVisualization';
import ErrorMessage from '../../../components/ErrorMessage';
import Metadata from '../../../components/Metadata';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal';
import Spinner from '../../../components/Spinner';
import SuperAdminArea from '../../../components/SuperAdmin/SuperAdminArea';
import { Page } from '../../../constants/pageTitles';
import { toISODate, todayISODate } from '../../../helpers/dateUtils';
import useFetch from '../../../helpers/useFetch';

const page = `${Page['abTests']}`;

// Returns ISO date string for the ab test start date
const defaultStartDate = (abTest) => {
  return toISODate(abTest.periodStart);
};

// Returns ISO date string for the ab test end date (or today if still running)
const defaultEndDate = (abTest) => {
  if (abTest.periodEnd) {
    return toISODate(abTest.periodEnd);
  }
  return todayISODate();
};

const AbTestsDetailsPage = () => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const { accountId, domainId, abTestId } = useParams();
  const [abTest, setAbTest] = useState();
  const [showConfiguration, setShowConfiguration] = useState();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [loadError, setLoadError] = useState();
  // Store ISO date strings (YYYY-MM-DD)
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [userError, setUserError] = useState();

  const isSuperAdmin = useIsSuperAdmin();

  const fetchUrl = `/api/admin/accounts/${accountId}/domains/${domainId}/abTests/${abTestId}`;

  useEffect(() => {
    fetch(fetchUrl)
      .then(({ abTest }) => {
        setAbTest(abTest);
        setStartDate(toISODate(abTest.periodStart));
        setEndDate(abTest.periodEnd ? toISODate(abTest.periodEnd) : todayISODate());
      })
      .catch(setLoadError);
  }, [fetch, fetchUrl]);

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (abTest === undefined) {
    return <Spinner />;
  }

  const stopAbTest = () => {
    fetch(fetchUrl, {
      method: 'PUT',
      body: JSON.stringify({ ...abTest, running: false }),
    })
      .then(({ abTest }) => setAbTest(abTest))
      .catch(setUserError);
  };

  const deleteAbTest = () => {
    fetch(fetchUrl, {
      method: 'DELETE',
    })
      .then(() => {
        navigate(`/accounts/${accountId}/domains/${domainId}/abTests`);
      })
      .catch(setUserError);
  };

  const backButton = (
    <Button variant="default" onClick={() => navigate(-1)}>
      <IconArrowLeft /> Back
    </Button>
  );

  const stopButton = (
    <Button onClick={() => setConfirmStop(true)}>
      <IconPlayerStop size={14} /> Stop
    </Button>
  );

  const deleteButton = (
    <Button color="red" onClick={() => setConfirmDelete(true)}>
      <IconTrash size={14} /> Delete
    </Button>
  );

  const formattedStartDate = new Date(abTest.periodStart).toDateString();
  const formattedEndDate = abTest.periodEnd ? new Date(abTest.periodEnd).toDateString() : 'now';
  const abTestPeriod = `A/B Test period: ${formattedStartDate} - ${formattedEndDate}`;

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics` },
    { title: page, href: `#/accounts/${accountId}/domains/${domainId}/abTests` },
    { title: `A/B test started at: ${new Date(abTest.periodStart).toDateString()}` },
    { title: 'Details' },
  ].map((item, index) => (
    <Anchor href={item.href} key={index}>
      {item.title}
    </Anchor>
  ));

  return (
    <>
      <Metadata title={`${page} | A/B test started at: ${new Date(abTest.periodStart).toDateString()} | Edit`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>

      <ConfirmationModal
        show={confirmDelete}
        close={() => setConfirmDelete(false)}
        confirm={() => deleteAbTest()}
        message="Are you sure you want to delete the A/B test?"
      />

      <ConfirmationModal
        show={confirmStop}
        close={() => setConfirmStop(false)}
        confirm={() => {
          stopAbTest();
          setConfirmStop(false);
        }}
        message="Are you sure you want to stop the A/B test?"
      />

      <Card withBorder mb="md" onClick={() => setShowConfiguration(!showConfiguration)} style={{ cursor: 'pointer' }}>
        <Card.Section inheritPadding py="xs">
          <Grid>
            <Grid.Col span={6}>
              <Text fw={700}>{abTestPeriod}</Text>
            </Grid.Col>

            <Grid.Col span={6} ta="right">
              <Text>
                Show configuration {showConfiguration ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
              </Text>
            </Grid.Col>
          </Grid>
        </Card.Section>
        <Collapse expanded={showConfiguration}>
          <Card.Section inheritPadding py="md" withBorder>
            <AbTestConfiguration abTest={abTest} readOnly />
          </Card.Section>
        </Collapse>
      </Card>

      <SuperAdminArea show={isSuperAdmin}>
        <AbTestSelector
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          minDate={defaultStartDate(abTest)}
          maxDate={defaultEndDate(abTest)}
        />
      </SuperAdminArea>

      <AbTestVisualization abTest={abTest} startDate={startDate} endDate={endDate} isSuperAdmin={isSuperAdmin} />

      <Grid>
        <Grid.Col span={12}>
          <Group justify="flex-end" mt="md">
            {backButton}
            {abTest.running ? stopButton : deleteButton}
          </Group>
        </Grid.Col>
      </Grid>
      {userError ? (
        <Box ta="right">
          <ErrorMessage message={userError.message} />
        </Box>
      ) : null}
    </>
  );
};

export default AbTestsDetailsPage;
